// Gate intake follow-up writes: what lands on a delivery AFTER its row exists.
// Shared by the Gate Intake / Confirm Arrival page (Deliveries.tsx) and the
// offline queue's delivery runner (offlineQueue.ts), so a queued intake and a
// live one record the same things.
//
// Why each one exists (gate-intake data-loss fix, 2026-09-30):
//  - save_gate_intake_delivery (mig 20261019140000, the live body) has no
//    scale_photo_url column. The "Take photo of load" photo on a manual intake
//    is therefore set on the delivery once the RPC has created it. Before this
//    it was never read at all.
//  - Confirm Arrival's back-date is an UPDATE of received_at, which
//    deliveries_sensitive_col_gate may refuse for a non-manager. It goes as its
//    own write so a refusal can be reported without undoing the confirmation.
//  - A gate photo that could not upload leaves a line in the record's notes.
//    Nothing in the app can re-attach a note photo afterwards, so the record
//    must at least say one was taken and lost.

import type { SupabaseClient } from '@supabase/supabase-js'
import { updateWithVersion, isStaleWriteError } from '@/lib/optimistic'
import { withTimeout, withReadTimeout } from '@/lib/saveTimeout'

type Db = Pick<SupabaseClient, 'from'>

/**
 * Patch a delivery at the version it holds NOW, version-guarded.
 *
 * The caller did not just read the row, and never assumes a version: triggers
 * on the delivery and its loads can bump it between the write that created the
 * row and this one. One re-read absorbs a concurrent edit; a second stale
 * answer is a real race and throws.
 */
export async function patchDeliveryAtCurrentVersion(
  db: Db, deliveryId: string, patch: Record<string, unknown>,
): Promise<void> {
  for (let attempt = 0; attempt < 2; attempt++) {
    const { data, error } = await withReadTimeout(db.from('deliveries')
      .select('version').eq('delivery_id', deliveryId).maybeSingle(), 'gate intake: delivery version')
    if (error) throw error
    const version = (data as { version?: number } | null)?.version
    if (version == null) throw new Error('The delivery could not be found to update it.')
    try {
      await withTimeout(updateWithVersion('deliveries', { col: 'delivery_id', value: deliveryId }, version, patch,
        { db, select: 'delivery_id' }), 'gate intake: delivery follow-up')
      return
    } catch (e) {
      if (attempt === 0 && isStaleWriteError(e)) continue
      throw e
    }
  }
}

/**
 * The record's notes with a line saying a gate photo was taken and did not
 * upload. It is appended to whatever the operator typed, never replacing it,
 * and clipped to the column's 2 000 characters (deliveries_notes_max).
 */
export function withPhotoLossNote(notes: unknown, what: 'Delivery-note photo' | 'Load photo'): string {
  const line = `${what} was taken at the gate but did not upload.`
  const typed = typeof notes === 'string' ? notes.trim() : ''
  return (typed ? `${typed} — ${line}` : line).slice(0, 2000)
}
